/**
 * CodesTab — flat list of every code from the analysis with one-click
 * drill-down into where it was applied.
 *
 * Added in Phase 5 of the document-unification work: clients asked for
 * a place to scan all codes at a glance (the existing Codebook tab
 * groups them under categories, which is the right view for the
 * synthesis but the wrong view for quick code lookup). Clicking a row
 * opens the shared ``CodeQuotesModal`` so users land in the same
 * familiar drawer they already know from the Codebook tab.
 *
 * Data source: the ``/codes_quotes/`` endpoint, same source the
 * Codebook tab + CodeQuotesModal already use. Don't try to read from
 * ``item.codes_quotes_dict`` — the analysis-item shape doesn't expose
 * it at the top level.
 *
 * Sources inside each code can be transcript-shaped (``transcript_id`` +
 * ``turn_id``) or document-shaped (``document_id`` + ``paragraph_id``)
 * — the count doesn't care which.
 */
import CodeDetailsModal from '@components/interview/analysis-components/CodeDetailsModal';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { fetchCodeQuotes } from '@services/interview';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

/**
 * Normalize the heterogenous codes_quotes_dict shape into a flat
 * ``[{name, definition, occurrences, isCustom}]`` list.
 *
 * ``isCustom`` flags a code that came from the user's uploaded
 * codebook (apply-codebook path) — surfaced as a chip in the row so
 * users can tell at a glance which codes the LLM applied vs invented.
 */
function buildCodeRows(codesQuotesDict, customCodeNames) {
  if (!codesQuotesDict || typeof codesQuotesDict !== 'object') return [];
  const customSet = new Set((customCodeNames || []).map((c) => (c?.name || '').toLowerCase()));

  return Object.entries(codesQuotesDict)
    .map(([name, value]) => {
      const sources = Array.isArray(value) ? value : value?.sources || [];
      const definition = Array.isArray(value) ? '' : value?.definition || '';
      // Server writes `summary` alongside `definition` and `sources` on
      // each code entry (see qualitative_study/services/analysis.py:1069).
      // Legacy dicts without the field fall through to empty string, so
      // the column renders "no summary" placeholder — no separate
      // per-code fetch needed for the list view.
      const summary = Array.isArray(value) ? '' : value?.summary || '';
      return {
        name,
        definition,
        summary,
        occurrences: sources.length,
        isCustom: customSet.has(name.toLowerCase()),
      };
    })
    .sort((a, b) => b.occurrences - a.occurrences || a.name.localeCompare(b.name));
}

const CodesTab = ({ item }) => {
  const { id: studyId } = useParams();
  const [search, setSearch] = useState('');
  const [openCode, setOpenCode] = useState(null);

  // Fetch the codes_quotes_dict from the same endpoint Codebook +
  // CodeQuotesModal already consume — keeps cache hot across tabs.
  const { data: codeQuotes, isPending } = useQuery({
    queryKey: ['interview-analysis-code', studyId],
    queryFn: () => fetchCodeQuotes({ studyId }),
    enabled: !!studyId,
  });

  const rows = useMemo(
    () => buildCodeRows(codeQuotes?.codes_quotes_dict, item?.custom_codes),
    [codeQuotes?.codes_quotes_dict, item?.custom_codes],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.definition || '').toLowerCase().includes(q) ||
        (r.summary || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  if (isPending) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No codes available yet. Run the analysis to generate the codebook.
        </Typography>
      </Box>
    );
  }

  const handleRowClick = (row) => {
    setOpenCode({ name: row.name, definition: row.definition });
  };

  return (
    <Box p={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6">All codes</Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length} {rows.length === 1 ? 'code' : 'codes'} from this analysis. Click any row
            to see every place it was applied.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search codes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
      </Stack>

      <Card variant="outlined">
        <TableContainer sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '22%' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Definition</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">
                  Occurrences
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => (
                <TableRow
                  key={row.name}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MuiLink
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        sx={{
                          fontWeight: 500,
                          textAlign: 'left',
                          color: 'primary.main',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          // Stop bubbling so we don't fire handleRowClick
                          // twice (the row's onClick + this Link click).
                          e.stopPropagation();
                          handleRowClick(row);
                        }}
                      >
                        {row.name}
                      </MuiLink>
                      {row.isCustom ? (
                        <Chip
                          size="small"
                          color="primary"
                          label="Custom"
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {row.definition || (
                      <Typography variant="caption" color="text.disabled">
                        (no definition)
                      </Typography>
                    )}
                  </TableCell>
                  {/* Summary — clamped to two lines so long text doesn't
                      bloat row height; the modal (opens on row click)
                      shows the full text with its own see-more toggle. */}
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {row.summary ? (
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {row.summary}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        (no summary)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{row.occurrences}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No codes match &ldquo;{search}&rdquo;.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <CodeDetailsModal
        open={Boolean(openCode)}
        onClose={() => setOpenCode(null)}
        code={openCode}
        codesQuotesDict={codeQuotes?.codes_quotes_dict}
      />
    </Box>
  );
};

export default CodesTab;
